import { PersonType, ThingActivity, ThingEventSnapshot, ThingType } from '@eagle/core-data-types';
import { FaultEventTypes } from '@eagle/data-function-types';
import { Alert, Button, Card, CardContent, CardHeader, Collapse, Divider, IconButton, Stack, styled, Tab, TabContext, TabPanel, TextField, Typography } from '@ethings/ui-toolkit';
import { Autocomplete, Box, Link, Tabs, useTheme } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import React, { Dispatch, FC, ReactNode, SetStateAction, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useHref } from 'react-router';
import { T_MANY } from '../../constants';
import { DISPLAY_ACTIVITIES_FEATURE_FLAG, FAULTS_VIEW_FLAG } from '../../feature-flags';
import { useCustomRoutes } from '../../hooks';
import { CacheDataTypes, Maybe, Undefinable } from '../../types';
import { DateTimeRangePickerProvider, testid, useLocalStorage } from '../../util';
import { ErrorBoundary } from '../error-boundary';
import { useBoolFlag } from '../flags';
import { DynamicIcon, Icon } from '../icons';
import { InlineDateTimeRangePicker } from '../inline-date-time-range-picker/inline-date-time-range-picker';
import { EventLocationData } from '../map';
import { MiddleSpinner } from '../middle-spinner';
import { SimpleElement } from '../simple-element';
import { OverflowTooltip } from '../tooltip';
import { ExportDataProps } from './entity-journey-item';
import { EntityItems } from './entity-journey-types';
import { JourneyActivity } from './journey-activity';
import { JourneyFaults } from './journey-faults';
import { HISTORY_DATE_RANGE_FLAG, HISTORY_DEFAULT_DATE_RANGE_UNIT, HISTORY_DEFAULT_MAX_DATE_RANGE, JourneyInstance } from './journey-instance';
import { JourneyInstanceExport } from './journey-instance-export';
import { JourneyInstanceNoDate } from './journey-instance-no-date';
import { useHistorySearch } from './use-history-search';

interface Props {
  'data-testid'?: string;
  entityDataType: ThingType | PersonType;
  entityDisplay: string;
  entityItem: EntityItems;
  handleMoreButtonClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleVisibilityButtonClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  hoveredEventId: Maybe<string>;
  isEntityVisible: boolean;
  isExporting: boolean;
  selectedEvent: Maybe<EventLocationData>;
  setExportData: Dispatch<SetStateAction<ExportDataProps[]>>;
  setHoveredEventId: Dispatch<SetStateAction<Maybe<string>>>;
  setSelectedEvent: Dispatch<SetStateAction<Maybe<EventLocationData>>>;
}

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: `${theme.spacing(2)} !important`,
}));

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
  padding: theme.spacing(2),
  '& .MuiCardHeader-action': { alignSelf: 'center', margin: 0 },
  '& .MuiCardHeader-content': { minWidth: 0 },
}));

const StyledIconButton = styled(IconButton)({
  padding: 0,
});

const StyledTypography = styled(Typography)({
  cursor: 'pointer',
});

export enum ActivityType {
  TRIP = 'trip',
  POWERED = 'powered',
  SPEEDING = 'speeding',
  GEOFENCE = 'geofence',
}

enum TrackHistoryPanelTab {
  ACTIVITIES = 'ACTIVITIES',
  EVENTS = 'EVENTS',
  FAULTS = 'FAULTS',
}

export enum SortBy {
  OLDEST_FIRST,
  LATEST_FIRST,
}

interface TrackHistoryPanelDefinition {
  content: ReactNode;
  label: string;
  badge?: string;
  isVisible: boolean;
}

export interface JourneyActivityData {
  fetchEventStartTime: Date;
  fetchEventEndTime: Date;
  thingActivities: ThingActivity[];
}

export const EntityCard: FC<Props> = ({
  entityDataType,
  entityDisplay,
  entityItem,
  handleMoreButtonClick,
  handleVisibilityButtonClick,
  hoveredEventId,
  isEntityVisible,
  isExporting,
  selectedEvent,
  setExportData,
  setHoveredEventId,
  setSelectedEvent,
  ...props
}) => {
  const theme = useTheme();
  const { addJourneyToEntity, closeAddDateTimePicker, dateAddingJourney, showLocationUpdateEvents, setShowLocationUpdateEvents } = useHistorySearch();
  const flags = useFlags();
  const maxDateRange = flags[HISTORY_DATE_RANGE_FLAG] as number || HISTORY_DEFAULT_MAX_DATE_RANGE;
  const { thing: customRoutesThing, person: customRoutesPerson } = useCustomRoutes();
  const href = useHref(`/${entityItem.entity === CacheDataTypes.THING ? customRoutesThing : customRoutesPerson}/${entityItem.id}`);
  const handleAddDateTime = (startDate: Date, endDate: Date): void => {
    addJourneyToEntity(startDate, endDate);
    closeAddDateTimePicker();
  };
  const [historyViewPreference, setHistoryViewPreference] = useLocalStorage<TrackHistoryPanelTab>('history_view_preference_v2', TrackHistoryPanelTab.ACTIVITIES);
  const [historySortPreference, setHistorySortPreference] = useLocalStorage<SortBy>('history_sort_preference_v2', SortBy.OLDEST_FIRST);
  const sortBy = historySortPreference;
  const displayActivitiesFeatureFlag = useBoolFlag(DISPLAY_ACTIVITIES_FEATURE_FLAG);
  const faultsViewFeatureFlag = useBoolFlag(FAULTS_VIEW_FLAG);
  const hasTabbedLayout = entityItem.entity === CacheDataTypes.THING;

  const defaultTab = (displayActivitiesFeatureFlag && hasTabbedLayout)
    ? TrackHistoryPanelTab.ACTIVITIES
    : TrackHistoryPanelTab.EVENTS;

  const { t } = useTranslation(['common', 'track']);
  const [journeyEvents, setJourneyEvents] = useState<Record<string, Undefinable<ThingEventSnapshot[]>>>({});
  const [journeyActivityData, setJourneyActivityData] = useState<Record<string, Undefinable<JourneyActivityData>>>({});

  const activityTypes = useMemo(() => {
    const allThingActivities = _.flatMap(Object.values(journeyActivityData).map((item) => item?.thingActivities || []));

    return Object.keys(_.groupBy(allThingActivities, (item) => item.activityType)) as ActivityType[];
  }, [journeyActivityData]);

  const noActivityTypesAvailable = !activityTypes.length;

  const headerActions = [
    {
      icon: isEntityVisible ? <Icon icon="Eye" data-testid="eye-icon" /> : <Icon icon="EyeOff" data-testid="icon-eye-off" />,
      onClick: handleVisibilityButtonClick,
      testId: 'eye-action-button',
    },
    {
      icon: <Icon icon="DotMenuVertical" data-testid="icon-dot-menu-vertical" />,
      onClick: handleMoreButtonClick,
      testId: 'menu-action-button',
    },
  ];

  const [activityTypeFilter, setActivityTypeFilter] = useState<ActivityType[]>([]);

  const journeyList = useMemo(() => {
    return entityItem.journeyList.sort((a, b) => sortBy === SortBy.LATEST_FIRST
      ? b.dateRange.endTime.getTime() - a.dateRange.endTime.getTime()
      : a.dateRange.startTime.getTime() - b.dateRange.startTime.getTime(),
    );
  }, [entityItem.journeyList, sortBy]);

  const faultEventsByJourney = useMemo(() => {
    return journeyList.reduce<Record<string, ThingEventSnapshot[]>>((result, journey) => {
      const events = journeyEvents[journey.id];
      if (events) {
        result[journey.id] =
          events.
            filter((event) => event.feature === 'fault/dtc' && event.eventTypeId === FaultEventTypes.UPDATE)
            .sort((a, b) => sortBy === SortBy.OLDEST_FIRST
              ? a.occurred.getTime() - b.occurred.getTime()
              : b.occurred.getTime() - a.occurred.getTime(),
            );
      }
      return result;
    }, {});
  }, [journeyList, journeyEvents, sortBy]);

  const numberOfFaults = useMemo(() => {
    return Object.values(faultEventsByJourney).reduce((result, faultEvents) => {
      result = result + faultEvents.length;
      return result;
    }, 0);
  }, [faultEventsByJourney]);

  const renderEvents = (tab = TrackHistoryPanelTab.EVENTS): JSX.Element => {
    return (
      <Stack spacing={1}>
        {journeyList.length
          ? journeyList.map((journey, index) => {
            const { thingActivities } = journeyActivityData[journey.id] || {};
            let fetchEventStartTime = journeyActivityData[journey.id]?.fetchEventStartTime;
            let fetchEventEndTime = journeyActivityData[journey.id]?.fetchEventEndTime;

            if (!displayActivitiesFeatureFlag || !hasTabbedLayout) {
              fetchEventStartTime = journey.dateRange.startTime;
              fetchEventEndTime = journey.dateRange.endTime;
            }

            return (
              <React.Fragment key={journey.id}>
                {fetchEventStartTime
                  && fetchEventEndTime
                  && <JourneyInstance
                    collapsible={tab === TrackHistoryPanelTab.EVENTS}
                    data-testid={`journey-instance-${index}`}
                    data={journey}
                    entityDisplay={entityDisplay}
                    fetchEventEndTime={fetchEventEndTime}
                    fetchEventStartTime={fetchEventStartTime}
                    hoveredEventId={hoveredEventId}
                    selectedEvent={selectedEvent}
                    setHoveredEventId={setHoveredEventId}
                    setJourneyEvents={setJourneyEvents}
                    setSelectedEvent={setSelectedEvent}
                    setShowLocationUpdateEvents={setShowLocationUpdateEvents}
                  />
                }
                {tab === TrackHistoryPanelTab.ACTIVITIES
                  && thingActivities
                  && !thingActivities.length
                  && <Alert severity="info">{t('track:page.history.activities.no-activities.hint.info')}</Alert>
                }
                {tab === TrackHistoryPanelTab.FAULTS && (
                  <>
                    {faultEventsByJourney[journey.id] ? (
                      <>
                        {faultEventsByJourney[journey.id].length === 0 && <Alert severity="success">{t('track:page.history.activities.no-faults.hint.info')}</Alert>}
                      </>
                    )
                      : <MiddleSpinner />
                    }
                  </>
                )}
                {isExporting
                  && <JourneyInstanceExport
                    data={journey}
                    onLoaded={(exportData) => {
                      setExportData((prev) => {
                        const newState = [...prev];
                        newState[index] = exportData;
                        return newState;
                      });
                    }}
                    showLocationUpdateEvents={showLocationUpdateEvents[journey.id]}
                  />
                }
              </React.Fragment>
            );
          })
          : <JourneyInstanceNoDate entityId={entityItem.id} />
        }
        <Collapse in={dateAddingJourney?.entityId === entityItem.id} unmountOnExit>
          <DateTimeRangePickerProvider>
            <InlineDateTimeRangePicker
              data-testid="date-time-picker"
              closeDateTimePicker={closeAddDateTimePicker}
              hideSeconds
              maxDateRange={maxDateRange}
              maxRangeUnit={HISTORY_DEFAULT_DATE_RANGE_UNIT}
              onDateRangeChanged={handleAddDateTime}
            />
          </DateTimeRangePickerProvider>
        </Collapse>
      </Stack>
    );
  };

  const tabDefinitions: Record<TrackHistoryPanelTab, TrackHistoryPanelDefinition> = {
    ACTIVITIES: {
      isVisible: Boolean(displayActivitiesFeatureFlag),
      label: t('track:page.history.activities.labels'),
      content: <>
        {!noActivityTypesAvailable && <>
          <Box>
            <MemoizedActivityTypeFilter
              activityTypes={activityTypes}
              noActivityTypesAvailable={noActivityTypesAvailable}
              onFilterChange={setActivityTypeFilter}
            />
            <Stack spacing={1} mt={2}>
              <Typography variant='body2'
                color="text.secondary">{t('track:page.history.activities.sort-by.labels')}</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                {[
                  {
                    display: t('track:page.history.activities.oldest-first.labels'),
                    value: SortBy.OLDEST_FIRST,
                  },
                  {
                    display: t('track:page.history.activities.latest-first.labels'),
                    value: SortBy.LATEST_FIRST,
                  },
                ].map((item) => {
                  return <Button
                    key={item.value}
                    variant={item.value === sortBy ? 'contained' : 'outlined'}
                    disableElevation
                    onClick={() => setHistorySortPreference(item.value)}
                  >
                    {item.display}
                  </Button>;
                })}
              </Stack>
            </Stack>
          </Box>
          <Box my={2} mx={0}>
            <Divider />
          </Box>
        </>}
        {renderEvents(TrackHistoryPanelTab.ACTIVITIES)}
      </>,
    },
    EVENTS: {
      isVisible: true,
      label: t('common:terms.event', { count: T_MANY }),
      content: <>{renderEvents(TrackHistoryPanelTab.EVENTS)}</>,
    },
    FAULTS: {
      isVisible: Boolean(faultsViewFeatureFlag && 'features' in entityDataType && entityDataType.features.find((feature) => feature.featureId === 'fault')),
      label: t('track:page.history.faults.labels'),
      badge: numberOfFaults > 0 ? `${numberOfFaults}` : undefined,
      content: (
        <Box>
          {numberOfFaults > 0 && (
            <>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">{t('track:page.history.activities.sort-by.labels')}</Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {[
                    {
                      display: t('track:page.history.activities.oldest-first.labels'),
                      value: SortBy.OLDEST_FIRST,
                      testId: 'fault-sort-oldest-first',
                    },
                    {
                      display: t('track:page.history.activities.latest-first.labels'),
                      value: SortBy.LATEST_FIRST,
                      testId: 'fault-sort-latest-first',
                    },
                  ].map((item) => {
                    return <Button
                      key={item.value}
                      data-testid={item.testId}
                      variant={item.value === sortBy ? 'contained' : 'outlined'}
                      disableElevation
                      onClick={() => setHistorySortPreference(item.value)}
                    >
                      {item.display}
                    </Button>;
                  })}
                </Stack>
              </Stack>
              <Box my={2} mx={0}>
                <Divider />
              </Box>
            </>
          )}
          {renderEvents(TrackHistoryPanelTab.FAULTS)}
        </Box>
      ),
    },
  };

  const tabs = Object.entries(tabDefinitions).filter(([_, { isVisible }]) => isVisible);

  const [selectedView, setSelectedView] = useState<TrackHistoryPanelTab>(
    ((displayActivitiesFeatureFlag || faultsViewFeatureFlag) && hasTabbedLayout && tabs.find(([tab]) => tab === historyViewPreference))
      ? historyViewPreference
      : defaultTab,
  );

  return (
    <TabContext value={selectedView}>
      <Card data-testid={props['data-testid']}>
        <StyledCardHeader
          action={
            <Stack direction="row" spacing={1}>
              {headerActions.map(({ icon, onClick, testId }, i) =>
                <StyledIconButton key={i} onClick={onClick} data-testid={testId}>
                  {icon}
                </StyledIconButton>,
              )}
            </Stack>
          }
          avatar={
            entityItem.entity === CacheDataTypes.THING
              ? <DynamicIcon icon={<Icon icon="ThingIcon" data-testid="thing-icon" />} color={theme.palette.text.secondary} />
              : <DynamicIcon icon={<Icon icon="PersonIcon" data-testid="person-icon" />} color={theme.palette.text.secondary} />
          }
          title={
            <Stack>
              <StyledTypography
                color="text.secondary"
                component="label"
                fontSize="0.8rem"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                <SimpleElement data={entityDataType} />
              </StyledTypography>
              <Link
                component="a"
                href={href}
                underline="none"
              >
                <OverflowTooltip
                  sx={{
                    fontSize: '1rem',
                    lineHeight: '1.5',
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}
                >
                  {entityDisplay}
                </OverflowTooltip>
              </Link>
            </Stack>
          }
        />
        {tabs.length > 1
          && hasTabbedLayout
          ? <>
            <Tabs
              onChange={(_, value) => {
                setHistoryViewPreference(value as TrackHistoryPanelTab);
                setSelectedView(value as TrackHistoryPanelTab);
              }}
              sx={{ pl: 1 }}
              value={selectedView}
              variant="fullWidth"
            >
              {tabs.map(([value, tab]) => (
                <Tab key={value} value={value} label={<Stack component="span" direction="row" spacing={1}><span>{tab.label}</span>{tab.badge !== undefined ? <TabBadge>{tab.badge}</TabBadge> : null}</Stack>} data-testid={testid`track-history-tab-${value}`} />
              ))}
            </Tabs>
            <Divider />
            {tabs.map(([value, tab]) => (
              <TabPanel key={value} keepMounted value={value} sx={{ p: '16px !important' }} >
                {tab.content}
              </TabPanel>
            ))}
          </>
          : <>
            <Divider />
            <StyledCardContent>
              {renderEvents()}
            </StyledCardContent>
          </>
        }
      </Card >
      {tabDefinitions.ACTIVITIES.isVisible
        && hasTabbedLayout
        && <TabPanel keepMounted value={TrackHistoryPanelTab.ACTIVITIES} sx={{ p: '0px !important' }}>
          {displayActivitiesFeatureFlag
            && !!journeyList.length
            && journeyList.map((journey) => (
              <React.Fragment key={journey.id}>
                <JourneyActivity
                  dateRangeFinish={journey.dateRange.endTime}
                  dateRangeStart={journey.dateRange.startTime}
                  filterBy={activityTypeFilter}
                  journey={journey}
                  setJourneyActivityData={setJourneyActivityData}
                  sortBy={sortBy}
                  thingEvents={journeyEvents[journey.id] || []}
                  thingId={journey.entityId}
                />
                <Box my={0} mx={-3}>
                  <Divider />
                </Box>
              </React.Fragment>
            ))
          }
        </TabPanel>
      }
      {
        tabDefinitions.FAULTS.isVisible && hasTabbedLayout && (
          <TabPanel keepMounted value={TrackHistoryPanelTab.FAULTS} sx={{ p: '0px !important' }}>
            {journeyList.map((journey) => (
              <React.Fragment key={journey.id}>
                <ErrorBoundary key={selectedView} fallback={(
                  <Box py={2}>
                    <Alert severity="error">
                      {t('common:page.errors.system-error.title')}
                    </Alert>
                  </Box>
                )}>
                  <JourneyFaults
                    faultEvents={(faultEventsByJourney[journey.id] ?? [])}
                    startTime={journeyActivityData[journey.id]?.fetchEventStartTime ?? journey.dateRange.startTime}
                    endTime={journeyActivityData[journey.id]?.fetchEventEndTime ?? journey.dateRange.endTime}
                  />
                </ErrorBoundary>
                <Box mx={-3}>
                  <Divider />
                </Box>
              </React.Fragment>
            ))}
          </TabPanel>
        )
      }
    </TabContext >
  );
};

const TabBadge = styled('span')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minWidth: 20,
  height: 20,
  borderRadius: 10,
  paddingRight: theme.spacing(0.25),
  paddingLeft: theme.spacing(0.25),
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  lineHeight: 1,
  ...theme.typography.caption,
}));

const ActivityTypeFilter: FC<{
  activityTypes: ActivityType[];
  noActivityTypesAvailable: boolean;
  onFilterChange: (selectedTypes: ActivityType[]) => void;
}> = ({ activityTypes, noActivityTypesAvailable, onFilterChange }) => {
  const { t } = useTranslation(['track']);

  return (
    <Autocomplete<ActivityType, true>
      multiple
      options={activityTypes}
      getOptionLabel={(activityType) => t(`track:activity-descriptions.${activityType}.label`)}
      filterSelectedOptions
      renderInput={(params) => (
        <TextField
          {...params}
          label={noActivityTypesAvailable
            ? t('track:page.history.activity-filter.no-activity-types.labels')
            : t('track:page.history.activities.activity-filter.labels')
          }
        />
      )}
      onChange={(_, selectedTypes) => onFilterChange(selectedTypes)}
      size="small"
      disabled={noActivityTypesAvailable}
    />
  );
};

const MemoizedActivityTypeFilter = React.memo(ActivityTypeFilter);
